import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import StripeConnectCard from "@/components/StripeConnectCard";

/**
 * Phase 7.1 — Wallet & Coin Packs
 *
 * - Shows the user's current balance + lifetime purchase/earning totals.
 * - Lists 4 purchasable Coin Packs (Starter, Fan Boost, Super Supporter,
 *   GOAT). Clicking a pack hits /api/coins/checkout and redirects to
 *   Stripe Checkout. Stripe bounces the user back to
 *   /wallet?coins_session={CHECKOUT_SESSION_ID}.
 * - On return with ?coins_session=..., we poll /api/coins/status/{id} up
 *   to 5 times (every 2s). The backend idempotently credits the wallet
 *   when the session is paid — duplicate polls & webhook hits are safe.
 * - Lists the purchase history below. Admin accounts also get a
 *   "Grant 5k Test Coins" button for QA.
 */

const PACK_ACCENTS = {
  starter: "from-amber-500/15 to-yellow-500/15 border-amber-500/40",
  fan_boost: "from-pink-500/15 to-rose-500/15 border-pink-500/40",
  super_supporter: "from-purple-500/20 to-fuchsia-500/15 border-purple-500/40",
  goat_supporter: "from-yellow-400/25 to-amber-500/20 border-yellow-400/60",
};

const REWARD_LABELS = {
  spotlight_winner_1: { icon: "🥇", label: "Spotlight #1 win" },
  spotlight_winner_2: { icon: "🥈", label: "Spotlight #2 win" },
  spotlight_winner_3: { icon: "🥉", label: "Spotlight #3 win" },
  referral_signup: { icon: "🎉", label: "Referral signup" },
  referral_first_track: { icon: "🎶", label: "Referral first track" },
  referral_featured: { icon: "🏆", label: "Referral featured" },
  crew_seed_join: { icon: "🌱", label: "Crew seed join" },
  crew_seeder_badge_unlocked: { icon: "🌱", label: "Crew Seeder badge" },
  crew_milestone_10: { icon: "📈", label: "Crew @ 10 members" },
  crew_milestone_25: { icon: "📈", label: "Crew @ 25 members" },
  crew_milestone_50: { icon: "📈", label: "Crew @ 50 members" },
};

const WalletPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [streak, setStreak] = useState(null);
  const [packs, setPacks] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [rewardsData, setRewardsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null); // pack_id in-flight
  const [granting, setGranting] = useState(false);
  const [pollingSession, setPollingSession] = useState(null);

  const isAdmin = user?.role === "admin";

  const refreshWallet = async () => {
    try {
      const r = await api.get("/wallet/me");
      setWallet(r.data?.wallet || null);
    } catch (_e) {
      setWallet(null);
    }
  };

  const refreshStreak = async () => {
    try {
      const r = await api.get("/gifts/streak/me");
      setStreak(r.data || null);
    } catch (_e) {
      setStreak(null);
    }
  };

  const refreshPurchases = async () => {
    try {
      const r = await api.get("/wallet/purchases?limit=25");
      setPurchases(r.data?.purchases || []);
    } catch (_e) {
      setPurchases([]);
    }
  };

  const refreshRewards = async () => {
    try {
      const r = await api.get("/rewards/me");
      setRewardsData(r.data || null);
    } catch (_e) {
      setRewardsData(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await api.get("/auth/me");
        if (!cancelled) setUser(me.data);
      } catch (_e) {
        if (!cancelled) {
          navigate("/login", { replace: true });
          return;
        }
      }
      const pk = await api.get("/coins/packs").catch(() => ({ data: { packs: [] } }));
      if (!cancelled) setPacks(pk.data?.packs || []);
      await refreshWallet();
      await refreshPurchases();
      await refreshStreak();
      await refreshRewards();
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle the return-from-Stripe ?coins_session= flow — poll status,
  // credit once, refresh balance, and clean the URL.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sess = params.get("coins_session");
    const canceled = params.get("coins_canceled");
    if (canceled) {
      toast.info("Purchase canceled");
      params.delete("coins_canceled");
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
      return;
    }
    if (!sess || pollingSession === sess) return;
    setPollingSession(sess);

    let attempts = 0;
    let stopped = false;
    const tick = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/coins/status/${sess}`);
        if (r.data?.credited) {
          toast.success(`🎉 +${r.data.coins?.toLocaleString() || ""} coins added to your wallet!`);
          await refreshWallet();
          await refreshPurchases();
          stopped = true;
          const p = new URLSearchParams(location.search);
          p.delete("coins_session");
          navigate({ pathname: location.pathname, search: p.toString() }, { replace: true });
          return;
        }
      } catch (_e) { /* keep polling */ }
      if (!stopped && attempts < 10) {
        setTimeout(tick, 2000);
      } else if (!stopped) {
        toast.info("We'll credit your coins as soon as Stripe confirms. Refresh in a moment.");
      }
    };
    tick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const buyPack = async (pack) => {
    if (buying) return;
    setBuying(pack.pack_id);
    try {
      const origin = window.location.origin;
      const r = await api.post("/coins/checkout", {
        pack_id: pack.pack_id,
        // Land on a dedicated return screen — it polls Stripe, shows a
        // confirmation card, and offers Wallet / Home / History links.
        origin_url: `${origin}/coins/return`,
      });
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        toast.error("Checkout URL missing");
        setBuying(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to start checkout");
      setBuying(null);
    }
  };

  const grantTest = async () => {
    setGranting(true);
    try {
      const r = await api.post("/wallet/grant-test-coins", {});
      setWallet(r.data?.wallet);
      toast.success(`+${r.data?.granted || 0} test coins added`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to grant test coins");
    } finally {
      setGranting(false);
    }
  };

  const balance = wallet?.balance ?? 0;
  const paidBalance = wallet?.paid_balance ?? 0;
  const rewardBalance = wallet?.reward_balance ?? 0;
  const testBalance = wallet?.test_balance ?? 0;
  const totalPurchased = wallet?.total_purchased ?? 0;
  const totalEarned = wallet?.total_earned_coins ?? 0;
  const availableEarnings = wallet?.withdrawable_earnings_available ?? 0;
  const pendingEarnings = wallet?.withdrawable_earnings_pending ?? 0;

  const sortedPacks = useMemo(
    () => [...packs].sort((a, b) => (a.price_usd || 0) - (b.price_usd || 0)),
    [packs]
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0514] via-[#0a0a14] to-black text-white">
      <div className="max-w-5xl mx-auto px-4 py-8" data-testid="wallet-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
              Your Wallet
            </h1>
            <p className="text-pink-200/80 text-sm mt-1">
              Top up your coins to support artists with gifts. Coins are a virtual currency and non-refundable.
            </p>
          </div>
          <Link
            to="/dashboard"
            className="text-xs text-pink-300 hover:text-pink-200 underline"
            data-testid="wallet-back-dashboard"
          >
            ← Dashboard
          </Link>
        </div>

        {/* Phase 17.0 — Stripe Connect Express card */}
        <StripeConnectCard variant="wallet" />

        {/* Phase 17.1 — Producer Dashboard quick link */}
        <Link
          to="/producer"
          className="block mb-4 rounded-xl border border-purple-500/30 bg-purple-500/5 hover:bg-purple-500/10 transition-colors px-4 py-3"
          data-testid="wallet-producer-dashboard-link"
        >
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-bold text-purple-100">💎 Producer Dashboard · Beat Earnings</div>
              <div className="text-xs text-gray-400 mt-0.5">Manage your beats, view sales, and request beat payouts.</div>
            </div>
            <span className="text-purple-200 text-lg">→</span>
          </div>
        </Link>

        {/* Balance summary — Phase 9.6 currency separation */}
        <Card className="bg-gradient-to-br from-amber-500/10 via-pink-500/10 to-purple-500/10 border-amber-500/40 p-6 mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div
              className="rounded-xl p-4 bg-gradient-to-br from-amber-500/20 to-amber-700/10 border border-amber-400/30"
              data-testid="wallet-paid-balance"
            >
              <div className="text-amber-200 text-[10px] uppercase tracking-wider font-bold flex items-center gap-1">
                💎 Paid Coins
                <span className="text-amber-200/60 normal-case font-normal">· cashout</span>
              </div>
              <div className="text-3xl font-black text-amber-100 mt-1">
                {paidBalance.toLocaleString()}
              </div>
              <p className="text-[10px] text-amber-200/60 mt-1">
                Bought with real money. Eligible to send gifts that create
                withdrawable creator earnings.
              </p>
            </div>
            <div
              className="rounded-xl p-4 bg-gradient-to-br from-purple-500/20 to-purple-700/10 border border-purple-400/30"
              data-testid="wallet-reward-balance"
            >
              <div className="text-purple-200 text-[10px] uppercase tracking-wider font-bold flex items-center gap-1">
                🪙 Reward Coins
                <span className="text-purple-200/60 normal-case font-normal">· non-cashout</span>
              </div>
              <div className="text-3xl font-black text-purple-100 mt-1">
                {rewardBalance.toLocaleString()}
              </div>
              <p className="text-[10px] text-purple-200/60 mt-1 flex items-center gap-1">
                <span data-testid="wallet-reward-coins-tooltip" title="Reward Coins are earned from RGM activities such as contests, referrals and platform rewards. They can be used inside RGM Studio but cannot be withdrawn as cash.">
                  Earned from quests, streaks, spotlight prizes. Use them to
                  gift, vote, and join events — <strong>not cashable</strong>.
                  <span aria-hidden="true" className="ml-1 text-purple-200/70 cursor-help">ⓘ</span>
                </span>
              </p>
            </div>
            {(testBalance > 0 || isAdmin) && (
              <div
                className="rounded-xl p-4 bg-gradient-to-br from-slate-500/20 to-slate-700/10 border border-slate-400/30"
                data-testid="wallet-test-balance"
              >
                <div className="text-slate-200 text-[10px] uppercase tracking-wider font-bold flex items-center gap-1">
                  🧪 Test Coins
                  <span className="text-slate-200/60 normal-case font-normal">· sandbox</span>
                </div>
                <div className="text-3xl font-black text-slate-100 mt-1">
                  {testBalance.toLocaleString()}
                </div>
                <p className="text-[10px] text-slate-200/60 mt-1">
                  Admin/QA grants. Never withdrawable, never count toward
                  creator earnings.
                </p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-white/10">
            <div data-testid="wallet-balance">
              <div className="text-amber-200/80 text-xs uppercase tracking-wider font-semibold">Total Balance</div>
              <div className="text-2xl font-black text-amber-200 mt-1">
                {balance.toLocaleString()} <span className="text-sm font-semibold text-amber-300/70">coins</span>
              </div>
            </div>
            <div data-testid="wallet-purchased">
              <div className="text-pink-200/80 text-xs uppercase tracking-wider font-semibold">Lifetime Purchased</div>
              <div className="text-2xl font-bold text-pink-200 mt-1">{totalPurchased.toLocaleString()}</div>
            </div>
            <div data-testid="wallet-earned">
              <div className="text-purple-200/80 text-xs uppercase tracking-wider font-semibold">Earned from Gifts <span className="text-purple-200/50 normal-case">· coins</span></div>
              <div className="text-2xl font-bold text-purple-200 mt-1">{totalEarned.toLocaleString()}</div>
              {availableEarnings + pendingEarnings > 0 && (
                <p className="text-[10px] text-purple-200/60 mt-1">
                  {availableEarnings.toLocaleString()} available · {pendingEarnings.toLocaleString()} pending hold
                </p>
              )}
              <p className="text-[10px] text-purple-200/50 mt-1">
                Not a cash balance. Only the verified paid-backed portion (shown as USD on
                {" "}<Link to="/earnings" className="underline decoration-dotted hover:text-purple-100">Earnings</Link>) is withdrawable.
              </p>
            </div>
          </div>

          {/* 💰 Payouts CTA */}
          {(totalEarned > 0 || availableEarnings + pendingEarnings > 0) && (
            <div className="mt-5 pt-5 border-t border-white/10 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-xs text-purple-200/80">
                💰 Ready to cash out? Only <strong className="text-purple-100">verified paid-backed</strong> earnings are withdrawable
                (minimum $25 · 7-day pending hold · rate $0.0075/coin).
              </div>
              <Link to="/payouts">
                <Button
                  size="sm"
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold"
                  data-testid="wallet-payouts-link"
                >
                  💰 Payouts →
                </Button>
              </Link>
            </div>
          )}

          {isAdmin && (
            <div className="mt-5 pt-5 border-t border-white/10 flex items-center justify-between gap-3">
              <div className="text-xs text-amber-200/70">
                🛠️ Admin QA: grant yourself test coins (hidden from regular users).
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={granting}
                onClick={grantTest}
                data-testid="wallet-grant-test-coins"
              >
                {granting ? "Granting…" : "+ 5,000 test coins"}
              </Button>
            </div>
          )}
        </Card>

        {/* 🎯 Phase 7.1 Milestone E — Daily Gift Streak summary */}
        {streak && (streak.streak_days > 0 || streak.longest_streak_days > 0) && (
          <Card
            className="bg-gradient-to-br from-orange-500/10 via-amber-500/10 to-rose-500/10 border-orange-500/40 p-5 mb-8"
            data-testid="wallet-streak-card"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-orange-200/80 text-[10px] uppercase tracking-wider font-semibold">
                  🎯 Daily Gift Streak <span className="text-orange-300/50">(UTC)</span>
                </p>
                <p className="text-3xl font-black text-orange-100 mt-1">
                  {streak.is_alive ? `${streak.streak_days}-Day Streak` : "0-Day Streak"}
                  <span className="text-base font-semibold text-orange-300/70"> {streak.is_alive ? "🔥" : "💤"}</span>
                </p>
                <p className="text-xs text-orange-200/70 mt-1">
                  {streak.is_alive
                    ? "Send at least one gift each UTC day to keep it alive."
                    : "Send a gift today to restart your streak."}
                  {streak.longest_streak_days > 0 && (
                    <> Longest ever: <span className="font-semibold text-orange-100">{streak.longest_streak_days}d</span>.</>
                  )}
                </p>
                {streak.next_milestone_at && (
                  <p className="text-[11px] text-rose-200 mt-1.5">
                    Next: <span className="font-semibold">{streak.next_milestone_at.badge?.emoji} {streak.next_milestone_at.badge?.label}</span>
                    {" "}in {streak.next_milestone_at.days_remaining} day{streak.next_milestone_at.days_remaining === 1 ? "" : "s"}
                    {" "}(+{streak.next_milestone_at.bonus_coins} bonus coins)
                  </p>
                )}
              </div>
              {Array.isArray(streak.badges) && streak.badges.length > 0 && (
                <div className="flex flex-wrap gap-1.5 max-w-[55%] justify-end">
                  {streak.badges.map((b) => (
                    <span
                      key={b.id}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/10 border border-white/15 text-xs font-semibold text-white"
                      title={`Unlocked at ${b.threshold} days`}
                      data-testid={`wallet-streak-badge-${b.id}`}
                    >
                      <span>{b.emoji}</span>
                      <span>{b.label}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Phase 15.6E — Rewards Earned (Spotlight wins + referrals + crew seeding etc.) */}
        {rewardsData && (rewardsData.rewards?.length > 0 || rewardsData.grand_total_coins > 0) && (
          <Card
            className="bg-gradient-to-br from-purple-500/10 via-pink-500/10 to-amber-500/10 border-purple-500/40 p-5 mb-8"
            data-testid="wallet-rewards-card"
          >
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <div>
                <p className="text-purple-200/80 text-[10px] uppercase tracking-wider font-semibold">
                  🏆 Rewards Earned
                </p>
                <p className="text-3xl font-black text-purple-100 mt-1" data-testid="wallet-rewards-total">
                  +{(rewardsData.grand_total_coins || 0).toLocaleString()}
                  <span className="text-base font-semibold text-purple-300/70"> coins · all-time</span>
                </p>
                <p className="text-xs text-purple-200/70 mt-1">
                  Coins you've earned from Spotlight wins, referrals, crew seeding, and milestones.
                </p>
              </div>
            </div>

            {/* Breakdown by type */}
            {rewardsData.totals_by_type && Object.keys(rewardsData.totals_by_type).length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4" data-testid="wallet-rewards-breakdown">
                {Object.entries(rewardsData.totals_by_type).map(([type, info]) => {
                  const meta = REWARD_LABELS[type] || { icon: "🪙", label: type };
                  return (
                    <div
                      key={type}
                      className="rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2"
                      data-testid={`wallet-reward-type-${type}`}
                    >
                      <div className="text-[10px] uppercase tracking-wider text-purple-200/70 flex items-center gap-1">
                        <span>{meta.icon}</span>
                        <span>{meta.label}</span>
                      </div>
                      <div className="text-amber-200 font-bold text-lg">
                        +{(info.total || 0).toLocaleString()}
                      </div>
                      <div className="text-[10px] text-gray-400">
                        × {info.count} grant{info.count === 1 ? "" : "s"}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Recent activity */}
            {rewardsData.rewards?.length > 0 && (
              <div data-testid="wallet-rewards-history">
                <p className="text-xs text-purple-200/70 font-semibold uppercase tracking-wider mb-2">
                  Recent
                </p>
                <ul className="space-y-1.5 max-h-[260px] overflow-y-auto pr-1">
                  {rewardsData.rewards.slice(0, 30).map((r) => {
                    const meta = REWARD_LABELS[r.reward_type] || { icon: "🪙", label: r.reward_type };
                    const d = r.created_at ? new Date(r.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";
                    return (
                      <li
                        key={r.reward_id || `${r.reward_type}-${r.created_at}`}
                        className="flex items-center justify-between gap-3 px-3 py-1.5 rounded-md bg-white/[0.03] hover:bg-white/[0.06] border border-white/5"
                        data-testid={`wallet-reward-row-${r.reward_id || r.reward_type}`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-lg flex-shrink-0">{meta.icon}</span>
                          <div className="min-w-0">
                            <div className="text-sm text-white truncate">{meta.label}</div>
                            {r.reason && (
                              <div className="text-[10px] text-gray-400 truncate">{r.reason}</div>
                            )}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-amber-200 font-bold text-sm">+{r.amount}</div>
                          <div className="text-[10px] text-gray-500">{d}</div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </Card>
        )}

        {/* Coin packs */}
        <h2 className="text-lg font-bold mb-3">Buy Coins</h2>
        {loading && packs.length === 0 ? (
          <div className="text-pink-200/60 text-sm">Loading packs…</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="coin-packs-grid">
            {sortedPacks.map((p) => {
              const total = (p.coins || 0) + (p.bonus_coins || 0);
              const accent = PACK_ACCENTS[p.pack_id] || "from-pink-500/15 to-purple-500/15 border-pink-500/40";
              const isGoat = p.pack_id === "goat_supporter";
              return (
                <Card
                  key={p.pack_id}
                  className={`relative p-5 bg-gradient-to-br ${accent} border hover:scale-[1.02] transition-transform`}
                  data-testid={`coin-pack-${p.pack_id}`}
                >
                  {p.label && (
                    <div className="absolute -top-2 left-3 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-pink-500 text-white shadow">
                      {p.label}
                    </div>
                  )}
                  <div className="text-center">
                    <div className={`text-5xl ${isGoat ? "" : ""}`}>
                      {p.pack_id === "starter" && "🪙"}
                      {p.pack_id === "fan_boost" && "💖"}
                      {p.pack_id === "super_supporter" && "💎"}
                      {p.pack_id === "goat_supporter" && "🐐"}
                    </div>
                    <div className="mt-2 font-bold text-white">{p.name}</div>
                    <div className="mt-2 text-2xl font-black text-amber-200">
                      {total.toLocaleString()}
                    </div>
                    <div className="text-[11px] text-amber-200/70">
                      coins{p.bonus_coins ? ` (${p.coins.toLocaleString()} + ${p.bonus_coins.toLocaleString()} bonus)` : ""}
                    </div>
                    <Button
                      className="mt-4 w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-400 hover:to-rose-400 text-white font-bold"
                      disabled={!!buying}
                      onClick={() => buyPack(p)}
                      data-testid={`coin-pack-buy-${p.pack_id}`}
                    >
                      {buying === p.pack_id ? "Redirecting…" : `Buy · $${p.price_usd.toFixed(2)}`}
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Purchase history */}
        <h2 className="text-lg font-bold mt-10 mb-3">Purchase History</h2>
        {purchases.length === 0 ? (
          <div
            className="text-pink-200/60 text-sm bg-white/5 border border-white/10 rounded-lg p-4"
            data-testid="wallet-no-purchases"
          >
            No purchases yet. Buy your first coin pack to light up the economy ✨
          </div>
        ) : (
          <div className="rounded-lg overflow-hidden border border-white/10" data-testid="wallet-purchase-history">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-pink-200/80 text-left">
                <tr>
                  <th className="px-3 py-2 font-semibold">Pack</th>
                  <th className="px-3 py-2 font-semibold">Coins</th>
                  <th className="px-3 py-2 font-semibold">Amount</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                  <th className="px-3 py-2 font-semibold">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {purchases.map((p) => {
                  const paid = p.credited || p.status === "paid";
                  const date = p.created_at ? new Date(p.created_at).toLocaleString() : "—";
                  return (
                    <tr key={p.session_id} data-testid={`purchase-row-${p.session_id}`}>
                      <td className="px-3 py-2 font-semibold text-white">{p.pack_name}</td>
                      <td className="px-3 py-2 text-amber-200">+{(p.total_coins || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 text-pink-200">${(p.amount_usd || 0).toFixed(2)}</td>
                      <td className="px-3 py-2">
                        <span
                          className={
                            paid
                              ? "text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 border border-emerald-500/40 uppercase tracking-wider font-bold"
                              : "text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-200 border border-amber-500/40 uppercase tracking-wider font-bold"
                          }
                        >
                          {paid ? "Paid" : p.status || "pending"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-400">{date}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <p className="mt-8 text-xs text-pink-200/50 text-center">
          Payments are processed securely by Stripe. Coins are a non-refundable virtual currency used to gift artists.
        </p>
      </div>
    </div>
  );
};

export default WalletPage;
